import { useEffect, useState } from "react";
import { Clock, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { imgUrl, rupiah } from "@/lib/api";
import { MenuImageViewer } from "@/components/MenuImageViewer";

const endOf = (d) => new Date(`${d}T23:59:59+07:00`).getTime();
const pad = (n) => String(n).padStart(2, "0");

const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
};

const Countdown = ({ ms, id }) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const parts = [[Math.floor(s / 86400), "hari"], [Math.floor((s % 86400) / 3600), "jam"], [Math.floor((s % 3600) / 60), "mnt"], [s % 60, "dtk"]];
  return (
    <div className="flex items-center gap-1.5" data-testid={`promo-banner-countdown-${id}`}>
      {parts.map(([v, l]) => (
        <div key={l} className="bg-white/15 rounded-lg px-2 py-1 text-center min-w-[44px]">
          <div className="font-bold text-lg leading-none tabular-nums">{l === "hari" ? v : pad(v)}</div>
          <div className="text-[10px] uppercase tracking-wide opacity-80">{l}</div>
        </div>
      ))}
    </div>
  );
};

const PromoCard = ({ item, now, wide, promos, menu }) => {
  const { add } = useCart();
  const [zoom, setZoom] = useState(false);
  const left = item.promo_end ? endOf(item.promo_end) - now : null;
  const onAdd = () => { add(item); toast.success(`${item.name} ditambahkan`, { duration: 1200 }); };
  return (
    <article className={`snap-start shrink-0 ${wide ? "w-full" : "w-[88%] sm:w-[560px]"} rounded-2xl bg-forest text-paper overflow-hidden flex flex-col sm:flex-row shadow-[0_18px_40px_-24px_rgba(21,94,173,0.8)]`} data-testid={`promo-banner-${item.id}`}>
      <button type="button" onClick={() => setZoom(true)} className="w-full sm:w-48 h-36 sm:h-auto shrink-0 cursor-zoom-in overflow-hidden" aria-label={`Perbesar gambar ${item.name}`} data-testid={`promo-banner-image-${item.id}`}>
        <img src={imgUrl(item.image)} alt={item.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
      </button>
      <MenuImageViewer items={promos} startId={item.id} open={zoom} onOpenChange={setZoom} menu={menu} />
      <div className="p-4 sm:p-5 flex flex-col gap-3 flex-1 min-w-0">
        <div>
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-amber-400 text-ink px-2 py-0.5 rounded-full"><Sparkles className="w-3 h-3" /> Promo paket</span>
          <h3 className="font-serif font-semibold text-xl sm:text-2xl mt-2 leading-tight" data-testid={`promo-banner-name-${item.id}`}>{item.name}</h3>
          {item.description && <p className="text-sm opacity-85 mt-1 line-clamp-2">{item.description}</p>}
        </div>
        {left !== null ? (
          <div>
            <div className="text-xs flex items-center gap-1 opacity-85 mb-1"><Clock className="w-3.5 h-3.5" /> Berakhir dalam</div>
            <Countdown ms={left} id={item.id} />
          </div>
        ) : <div className="text-xs opacity-85">Promo berlaku selama persediaan ada</div>}
        <div className="flex items-center justify-between gap-3 mt-auto">
          <div className="font-bold text-2xl" data-testid={`promo-banner-price-${item.id}`}>{rupiah(item.price)}</div>
          <button onClick={onAdd} className="bg-paper text-forest font-semibold text-sm px-4 py-2 rounded-full flex items-center gap-1.5 hover:bg-cream active:scale-95 transition-[background-color,transform]" data-testid={`promo-banner-add-${item.id}`}>
            <Plus className="w-4 h-4" /> Tambah
          </button>
        </div>
      </div>
    </article>
  );
};

export const PromoBanner = ({ menu }) => {
  const now = useNow();
  const promos = menu
    .filter((m) => m.category === "paket" && (!m.promo_end || endOf(m.promo_end) > now))
    .sort((a, b) => (a.promo_end || "9999").localeCompare(b.promo_end || "9999"));
  if (!promos.length) return null;
  return (
    <section className="wrap mt-10 sm:mt-14" data-testid="promo-banner-section">
      <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1">
        {promos.map((m) => <PromoCard key={m.id} item={m} now={now} wide={promos.length === 1} promos={promos} menu={menu} />)}
      </div>
    </section>
  );
};
